<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useGlobalStore } from '@stores/global'
import { useEventStore } from '@stores/event'
import { useRoute, useRouter } from 'vue-router'
import { format } from '@formkit/tempo'
import _ from 'lodash'
import Hero from '@sections/tournament/Hero.vue'
import Tag from '@components/global/Tag.vue'
import Button from '@components/forms/Button.vue'
import ContentFrame from '@components/global/ContentFrame.vue'
import ContentSection from '@components/global/ContentSection.vue'
import Text from '@components/global/Text.vue'
import Calendar from '@components/global/Calendar.vue'
import Toggle from '@components/forms/Toggle.vue'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'
import Loading from '@components/global/Loading.vue'
import Select from '@components/forms/Select.vue'
import Input from '@components/forms/Input.vue'
import EventCard from '@components/index/EventCard.vue'

const global = useGlobalStore()
const eventStore = useEventStore()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const tournament = ref({})
const requestTournament = ref(null)
const slug = computed(() => route.params.slug)

const activeTab = ref(null)

const tabsWrapper = ref(null)
const toggleRef = ref(null)
const tabsOverflow = ref(false)
let resizeObserver = null

const measureTabs = async () => {
	await nextTick()
	const el = toggleRef.value?.$el
	const wrapper = tabsWrapper.value
	if (!el || !wrapper) return
	tabsOverflow.value = el.scrollWidth > wrapper.clientWidth + 1
}

// Attach the observer once the tab wrapper is rendered (tabs load async).
watch(tabsWrapper, (wrapper) => {
	if (wrapper && resizeObserver) {
		resizeObserver.observe(wrapper)
	}
	measureTabs()
})

const slugify = (value) =>
	String(value || '')
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

// Dynamic content tabs driven by the model's `tabs` builder. Each entry becomes
// its own frontend tab, rendered from its ordered content blocks.
const dynamicTabs = computed(() =>
	(tournament.value.tabs ?? []).map((tab, i) => ({
		name: tab.name || `Tab ${i + 1}`,
		slug: slugify(tab.name) || `tab-${i + 1}`,
		content: tab.content ?? [],
	})),
)

const tabs = computed(() => [
	...dynamicTabs.value.map((tab) => ({ name: tab.name, slug: tab.slug, disabled: tab.content.length === 0 })),
	...(tournament.value.slug === '2026'
		? [
				{
					name: 'SWC 2026 Points Ranking',
					slug: 'points-ranking',
				},
			]
		: []),
])

function cancelTournament() {
	if (requestTournament.value) requestTournament.value.cancel()
}
function getTournament() {
	loading.value = true
	tournament.value = {}
	cancelTournament()
	let axiosSource = axios.CancelToken.source()
	requestTournament.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/tournament/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((a) => {
			// Redirect home when the event page is disabled for this tournament.
			if (a.data?.page === false) {
				router.push('/')
				return
			}
			loading.value = false
			tournament.value = a.data
			if (tabs.value.length > 0) {
				const hashSlug = route.hash?.slice(1)
				const matchedTab = tabs.value.find((tab) => tab.slug === hashSlug && !tab.disabled)
				const firstEnabledTab = tabs.value.find((tab) => !tab.disabled)
				activeTab.value = (matchedTab ?? firstEnabledTab ?? tabs.value[0]).slug
			}
		})
		.catch((e) => {
			if (e?.response?.status === 404) {
				router.push('/')
			}
		})
}
getTournament()

watch(slug, async () => {
	getTournament()
})

watch(activeTab, (newTab) => {
	if (newTab) {
		router.replace({ hash: `#${newTab}` })
	}
})

watch(tabs, () => {
	measureTabs()
})

//

const filters = ref({})

const leaderboardItems = computed(() => {
	return global.games.filter((game) => game.slug === filters.value.game)[0]?.leaderboard_items ?? []
})

const filteredItems = computed(() => {
	return leaderboardItems.value.filter((item) => {
		const searchTerm = (filters.value.search || '').toLowerCase()
		const searchMatch = !searchTerm || item.player.username.toLowerCase().includes(searchTerm)
		return searchMatch
	})
})

const clear = () => {
	filters.value = {}
}

onMounted(() => {
	if (global.games.length > 0) {
		filters.value.game = global.games[0].slug
	} else {
		global.getGames().then(() => {
			if (global.games.length > 0) {
				filters.value.game = global.games[0].slug
			}
		})
	}

	if (window.ResizeObserver) {
		resizeObserver = new ResizeObserver(() => measureTabs())
		tabsWrapper.value && resizeObserver.observe(tabsWrapper.value)
	}
	measureTabs()
	// Re-measure after web-font/layout settling.
	setTimeout(measureTabs, 200)
})

onBeforeUnmount(() => {
	resizeObserver?.disconnect()
})
//
</script>

<template>
	<Loading v-if="loading" />
	<main class="mt-24" v-else>
		<Hero :title="tournament.title || tournament.name" :description="tournament.description" :logo="tournament.logo" :poster="tournament.poster" :external-label="tournament.external_label" :external-url="tournament.external_url" />

		<div v-if="tabs?.length > 1">
			<div class="container">
				<div class="relative overflow-hidden" ref="tabsWrapper">
					<div :class="['mb-5', tabsOverflow ? 'invisible pointer-events-none absolute' : 'block']">
						<Toggle ref="toggleRef" :options="tabs" v-model="activeTab" />
					</div>

					<div class="mb-5 flex items-start justify-start" v-if="tabsOverflow">
						<Select class="primary w-full" v-model="activeTab">
							<option :value="tab.slug" v-for="tab in tabs" :disabled="tab.disabled">{{ tab.name }}</option>
						</Select>
					</div>
				</div>
			</div>
		</div>
		<section class="mb-10">
			<div class="container">
				<ContentFrame class="z-10" :header="{ type: 'compact', title: tabs.find((tab) => tab.slug == activeTab)?.name }" v-if="tabs?.length">
					<template v-for="tab in dynamicTabs" :key="tab.slug">
						<ContentSection v-if="activeTab == tab.slug">
							<template v-for="(block, i) in tab.content" :key="i">
								<Text v-if="block.type === 'text'" :content="block.data?.content" />
								<Calendar v-else-if="block.type === 'calendar'" :src="block.data?.src" />
								<div v-else-if="block.type === 'qualifiers'" class="flex flex-col gap-16">
									<div class="flex flex-col gap-3" v-if="tournament.qualifiers?.filter((event) => event.start_at && event.completed == false).length > 0">
										<div class="flex items-center justify-between">
											<h6>Upcoming</h6>
										</div>
										<div class="flex flex-col gap-2">
											<EventCard v-for="event in tournament.qualifiers?.filter((event) => event.start_at && event.completed == false)" :event="event" :key="event.slug" />
										</div>
									</div>
									<div class="flex flex-col gap-3" v-if="tournament.qualifiers?.filter((event) => event.start_at && event.completed == true).length > 0">
										<div class="flex items-center justify-between">
											<h6>Completed</h6>
										</div>
										<div class="flex flex-col gap-2">
											<EventCard
												v-for="event in _.orderBy(
													tournament.qualifiers?.filter((event) => event.start_at && event.completed == true),
													'start_at',
													'desc',
												)"
												:event="event"
												:key="event.slug" />
										</div>
									</div>
								</div>
							</template>
						</ContentSection>
					</template>

					<div class="my-5" v-if="activeTab == 'points-ranking'">
						<section>
							<div class="container">
								<div class="relative flex items-center gap-3">
									<div class="flex grow flex-col gap-3 md:flex-row">
										<Input class="secondary w-full" v-model="filters.search" placeholder="Search" />
										<Select class="secondary w-full" v-model="filters.game">
											<template v-for="game in global.games" :key="game.id">
												<option :value="game.slug" v-if="game.leaderboard_items.length > 0">{{ game.name }}</option>
											</template>
										</Select>
									</div>
									<div class="mx-2 h-12 w-px border-r border-black/15"></div>
									<div class="flex gap-3">
										<Button class="secondary" @click="clear">Clear</Button>
									</div>
								</div>
							</div>
						</section>
						<section class="my-10">
							<div class="my-3 px-2 text-sm">★ = Qualified for SWC 2026</div>

							<div class="container">
								<ContentFrame class="overflow-x-scroll">
									<table>
										<thead>
											<tr>
												<th>Rank</th>
												<th class="min-w-48">Player</th>
												<th>Total</th>
												<th>Legend</th>
												<th class="text-nowrap">Master 1</th>
												<th class="text-nowrap">Master 2</th>
												<th class="text-nowrap">Master 3</th>
												<th>Online</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="item in filteredItems" v-if="filteredItems.length > 0">
												<td :class="[{ first: item.rank === 1 }, { second: item.rank === 2 }, { third: item.rank === 3 }]">
													<div class="flex items-center gap-3">
														<i v-if="item.rank <= 3" :class="['fas fa-trophy leading-none', { 'text-[#DEBF77]': item.rank === 1 }, { 'text-[#C2C2C2]': item.rank === 2 }, { 'text-[#D39881]': item.rank === 3 }]" />
														#{{ item.rank }}
													</div>
												</td>
												<td class="py-4">
													<div class="flex items-center gap-3 text-nowrap">
														<img :src="item.player.avatar" class="from-snk-light-900 size-10 rounded-full bg-linear-to-t to-white object-cover object-top md:size-16" v-if="item.player.avatar" />
														{{ item.player.username }}
													</div>
												</td>
												<!-- <td>{{ item.player.country.flag }} {{ item.player.country.slug }}</td> -->
												<td>{{ item.total_points.toLocaleString('en-GB') }}</td>
												<td>{{ item.legend.toLocaleString('en-GB') }}</td>
												<td>{{ item.master_1.toLocaleString('en-GB') }}</td>
												<td>{{ item.master_2.toLocaleString('en-GB') }}</td>
												<td>{{ item.master_3.toLocaleString('en-GB') }}</td>
												<td>{{ item.online.toLocaleString('en-GB') }}</td>
											</tr>
											<tr v-else>
												<td colspan="6" class="text-center">There are no players matching your filters.</td>
											</tr>
										</tbody>
									</table>
								</ContentFrame>
							</div>
						</section>
					</div>

				</ContentFrame>
			</div>
		</section>
	</main>
</template>
